import React, { useEffect, useRef, useState } from "react";
import "@/components/sections/HeroHeader.css";
import { analytics, track } from "@/lib/analytics";
import { Countdown } from "@/components/Countdown";
import { eventConfig } from "@/config/eventConfig";
import { VideoDialog } from "@/components/sections/VideoDialog";

const MILESTONES = [
  {
    age: "55", label: "Retirement Account",
    cardOne: "BRS · FRS · ERS", qOne: "How much savings do I need for each CPF retirement payout tier?",
    cardTwo: "Withdrawal options", qTwo: "How much can I actually withdraw at 55 — and what must stay?",
    cardThree: "Plan ahead", qThree: "Will my savings today give me the monthly income I want later?",
  },
  {
    age: "65", label: "Payout eligibility",
    cardOne: "CPF LIFE", qOne: "How does CPF LIFE turn my savings into income for life?",
    cardTwo: "Choose your plan", qTwo: "Standard, Basic or Escalating — which plan fits my needs?",
    cardThree: "Start payouts", qThree: "Should I start payouts now, or defer for a higher amount?",
  },
  {
    age: "70", label: "Automatic Payout Starts",
    cardOne: "Confirm your plan", qOne: "Is my CPF LIFE plan still the right fit for me today?",
    cardTwo: "If not started earlier", qTwo: "What happens to my payouts if I haven't started them yet?",
    cardThree: "Review bank details", qThree: "Are my bank details correct so payouts arrive smoothly?",
  },
];

const NAV = [
  { label: "Watch", href: "#watch" },
  { label: "CPF Explorer", href: "#cpf-clarity" },
  { label: "Stories", href: "#past-events" },
  { label: "Agenda", href: "#agenda" },
  { label: "Speakers", href: "#speakers" },
  { label: "FAQ", href: "#faq" },
];

const HeroHeader = ({ onRegister }) => {
  const heroRef = useRef(null);
  const [active, setActive] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const m = MILESTONES[active];
  const { video } = eventConfig;

  const openVideo = () => {
    track("video_open", { source: "hero" });
    setVideoOpen(true);
  };

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const onMove = (event) => {
      const b = hero.getBoundingClientRect();
      const x = ((event.clientX - b.left) / b.width) * 100;
      const y = ((event.clientY - b.top) / b.height) * 100;
      hero.style.setProperty("--pointer-x", `${x}%`);
      hero.style.setProperty("--pointer-y", `${y}%`);
    };
    hero.addEventListener("pointermove", onMove);
    return () => hero.removeEventListener("pointermove", onMove);
  }, []);

  const goRegister = (e) => {
    if (e) e.preventDefault();
    analytics.primaryCtaClick({ source: "hero" });
    if (onRegister) return onRegister();
    const el = document.getElementById("register");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goto = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectAge = (i) => setActive(i);

  return (
    <section className="cpf-hero" id="cpf-hero" ref={heroRef} data-testid="hero">
      <div className="cpf-hero__background" aria-hidden="true">
        <picture>
          <source
            type="image/webp"
            srcSet="/retirement-hero-v2-900.webp 900w, /retirement-hero-v2.webp 1672w"
            sizes="100vw"
          />
          <img src="/retirement-hero-v2.png" alt="" loading="eager" fetchpriority="high" width="1672" height="941" />
        </picture>
      </div>
      <div className="cpf-hero__overlay" aria-hidden="true" />
      <div className="cpf-hero__grid-overlay" aria-hidden="true" />
      <div className="cpf-hero__pointer-glow" aria-hidden="true" />

      {/* Navigation */}
      <nav className="cpf-hero__nav" aria-label="Main navigation">
        <a href="#cpf-hero" className="cpf-hero__brand" aria-label="Affinity Group">
          <img className="cpf-hero__brand-logo" src="/affinity-logo.png" alt="Affinity Group" width="1092" height="663" />
        </a>
        <div className="cpf-hero__nav-links">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} onClick={(e) => goto(e, n.href)}>
              {n.label}
            </a>
          ))}
        </div>
        <a href="#register" className="cpf-hero__nav-cta" onClick={goRegister} data-testid="hero-nav-register">
          Reserve a Seat
          <span aria-hidden="true">&rarr;</span>
        </a>
      </nav>

      <div className="cpf-hero__container">
        {/* Left Content */}
        <div className="cpf-hero__content">
          <p className="cpf-hero__eyebrow">
            <span />
            Complimentary CPF Retirement Planning Seminar
          </p>
          <h1 className="cpf-hero__title" aria-label="You've contributed to CPF for years. But do you know what happens at 55?">
            <span className="cpf-hero__title-line cpf-hero__title-line--first" aria-hidden="true">
              <span className="cpf-hero__word"><b>You&rsquo;ve</b></span>
              <span className="cpf-hero__word"><b>Contributed</b></span>
              <span className="cpf-hero__word"><b>to</b></span>
              <span className="cpf-hero__word"><b>CPF</b></span>
              <span className="cpf-hero__word"><b>for</b></span>
              <span className="cpf-hero__word"><b>Years.</b></span>
            </span>
            <span className="cpf-hero__title-line" aria-hidden="true">
              <span className="cpf-hero__word"><b>But</b></span>
              <span className="cpf-hero__word"><b>Do</b></span>
              <span className="cpf-hero__word"><b>You</b></span>
              <span className="cpf-hero__word"><b>Know</b></span>
              <span className="cpf-hero__word"><b>What</b></span>
              <span className="cpf-hero__word"><b>Happens</b></span>
              <span className="cpf-hero__word cpf-hero__word--gold"><b>at</b></span>
              <span className="cpf-hero__word cpf-hero__word--gold"><b>55?</b></span>
            </span>
          </h1>
          <p className="cpf-hero__description" data-testid="hero-description">
            Most Singaporeans know they can <strong className="cpf-hero__hl">withdraw some CPF at 55</strong>. But what happens to the rest? And one day your salary stops &mdash; will your <strong className="cpf-hero__hl">retirement income</strong> be enough for the next <strong className="cpf-hero__hl">20, 30 or even 40 years</strong>?
          </p>
          <div className="cpf-hero__event-details">
            <div className="cpf-hero__event-item">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 3v4M17 3v4M4 9h16M5 5h14a1 1 0 0 1 1 1v14H4V6a1 1 0 0 1 1-1Z" />
              </svg>
              <span>
                <small>DATE</small>
                <strong>Thursday, 1 October 2026</strong>
              </span>
            </div>
            <div className="cpf-hero__event-item">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              <span>
                <small>TIME</small>
                <strong>7:00PM &ndash; 9:00PM</strong>
                <em className="cpf-hero__event-note" data-testid="hero-registration-time">Registration from 6:30PM</em>
              </span>
            </div>
          </div>
          <div className="cpf-hero__countdown">
            <Countdown variant="block" label="Seminar starts in" />
          </div>
          <div className="cpf-hero__actions">
            <div className="cpf-hero__cta-row">
              <a href="#register" className="cpf-hero__primary-cta" onClick={goRegister} data-testid="hero-primary-cta">
                Reserve My Complimentary Seat
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h13M13 6l6 6-6 6" />
                </svg>
              </a>
              <button type="button" className="cpf-hero__watch-cta" onClick={openVideo} data-testid="hero-watch-video">
                <span className="cpf-hero__watch-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" /></svg>
                </span>
                {video.watchLabel}
                <small>{video.durationLabel}</small>
              </button>
            </div>
            <p>CPF Board representative &middot; Practical examples &middot; Q&amp;A</p>
          </div>
        </div>

        {/* Interactive CPF Journey Panel */}
        <div className="cpf-hero__journey">
          <div className="cpf-hero__journey-header">
            <span>YOUR CPF JOURNEY</span>
            <span className="cpf-hero__live-label">
              <i />
              2-HOUR SESSION
            </span>
          </div>
          <div className="cpf-hero__age-tabs" role="tablist" aria-label="Explore CPF retirement milestones">
            {MILESTONES.map((mi, i) => (
              <button
                key={mi.age}
                type="button"
                className={`cpf-hero__age-tab${i === active ? " is-active" : ""}`}
                data-testid={`hero-age-tab-${mi.age}`}
                role="tab"
                aria-selected={i === active}
                onClick={() => selectAge(i)}
              >
                <small>AGE</small>
                <strong>{mi.age}</strong>
              </button>
            ))}
          </div>
          <div className="cpf-hero__orbit">
            <span className="cpf-hero__orbit-ring cpf-hero__orbit-ring--outer" />
            <span className="cpf-hero__orbit-ring cpf-hero__orbit-ring--inner" />
            <div className="cpf-hero__age-core is-changing" aria-live="polite">
              <small>AGE</small>
              <strong key={`age-${active}`}>{m.age}</strong>
              <span key={`lbl-${active}`}>{m.label}</span>
            </div>
            <div className="cpf-hero__floating-card cpf-hero__floating-card--one" tabIndex={0} data-testid="cpf-card-prepare">
              <small>PREPARE</small>
              <strong>{m.cardOne}</strong>
              <p className="cpf-hero__floating-q">{m.qOne}</p>
            </div>
            <div className="cpf-hero__floating-card cpf-hero__floating-card--two" tabIndex={0} data-testid="cpf-card-understand">
              <small>UNDERSTAND</small>
              <strong>{m.cardTwo}</strong>
              <p className="cpf-hero__floating-q">{m.qTwo}</p>
            </div>
            <div className="cpf-hero__floating-card cpf-hero__floating-card--three" tabIndex={0} data-testid="cpf-card-nextstep">
              <small>NEXT STEP</small>
              <strong>{m.cardThree}</strong>
              <p className="cpf-hero__floating-q">{m.qThree}</p>
            </div>
          </div>
          <div className="cpf-hero__venue">
            <div className="cpf-hero__venue-head">
              <span>VENUE</span>
              <div>
                <strong>Capital Group &middot; 1 Raffles Quay, #43 North Tower</strong>
                <small>Singapore 048583</small>
              </div>
            </div>
            <div className="cpf-hero__venue-map">
              <iframe
                title="Seminar venue location map"
                data-testid="hero-venue-map-iframe"
                src={eventConfig.event.mapEmbedUrl}
                width="100%"
                height="150"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>

      <VideoDialog open={videoOpen} onOpenChange={setVideoOpen} source="hero" />

      <div className="cpf-hero__trust-strip">
        <div>
          <p><strong>Practical.</strong> Not overwhelming.</p>
          <i />
          <p><strong>Educational.</strong> No jargon.</p>
          <i />
          <p><strong>Interactive.</strong> Ask questions live.</p>
        </div>
      </div>
    </section>
  );
};

export default HeroHeader;
